import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { useNavigate } from "react-router-dom";
import {
  DateCalendar,
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  findAllAcoesDisponiveisInscricao,
  findMinhasInscricoes,
} from "../../../API/apiAcoes";
import { findAvailableParticipantCertificates } from "../../../API/apiParticipantCertificates";
import { useAuth } from "../../../resources/AuthProvider";
import styles_button from "../../../styles/Button.module.css";

dayjs.locale("pt-br");

type InscricaoAgenda = {
  acaoId: number;
  nomeAcao: string;
  turmaId: number;
  nomeTurma: string;
  dataInicio: string;
  dataFim: string;
  modalidade: string;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { hasActionPermission, getDisplayName } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [totalAcoesDisponiveis, setTotalAcoesDisponiveis] = useState<number>(0);
  const [totalInscricoes, setTotalInscricoes] = useState<number>(0);
  const [totalCertificadosDisponiveis, setTotalCertificadosDisponiveis] = useState<number>(0);
  const [inscricoesAgenda, setInscricoesAgenda] = useState<InscricaoAgenda[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [agendaView, setAgendaView] = useState<"dia" | "semana">("dia");

  const canViewAcoesDisponiveis = hasActionPermission("acoes.disponiveis_inscricao_own");
  const canViewMinhasInscricoes = hasActionPermission("turmas.minhas_inscricoes_all");
  const canViewCertificados = hasActionPermission(
    "certificate_templates.meus_certificados_all",
  );

  const normalizarInscricao = (item: any): InscricaoAgenda => {
    const turma = item?.turma ?? item;
    const acao = item?.acao ?? {};

    return {
      acaoId: Number(acao?.id ?? item?.acao_id ?? turma?.acao_id ?? 0),
      nomeAcao: String(
        acao?.nome ?? item?.nome_acao ?? item?.acao_nome ?? "Acao de Qualificacao",
      ),
      turmaId: Number(turma?.id ?? item?.turma_id ?? item?.id ?? 0),
      nomeTurma: String(turma?.nome ?? item?.nome_turma ?? "Turma"),
      dataInicio: String(turma?.data_inicio ?? item?.data_inicio ?? ""),
      dataFim: String(turma?.data_fim ?? item?.data_fim ?? ""),
      modalidade: String(
        turma?.descricao_modalidade ??
          turma?.modalidade ??
          item?.descricao_modalidade ??
          item?.modalidade ??
          "-",
      ),
    };
  };

  const extrairListaInscricoes = (result: any): any[] => {
    return (
      (Array.isArray(result?.data?.inscricoes) && result.data.inscricoes) ||
      (Array.isArray(result?.inscricoes) && result.inscricoes) ||
      (Array.isArray(result?.data) && result.data) ||
      (Array.isArray(result) && result) ||
      []
    );
  };

  const carregarDashboard = async () => {
    try {
      setLoading(true);
      setMsgError(null);

      const [acoesResponse, inscricoesResponse, certificadosResponse] = await Promise.all([
        canViewAcoesDisponiveis
          ? findAllAcoesDisponiveisInscricao(1, 1)
          : Promise.resolve(null),
        canViewMinhasInscricoes ? findMinhasInscricoes(1, 300) : Promise.resolve(null),
        canViewCertificados ? findAvailableParticipantCertificates() : Promise.resolve(null),
      ]);

      const totalAcoes =
        Number(acoesResponse?.pagination?.total) ||
        (Array.isArray(acoesResponse?.acoes) ? acoesResponse.acoes.length : 0);

      const totalInscritos =
        Number(inscricoesResponse?.pagination?.total) ||
        Number(inscricoesResponse?.data?.pagination?.total) ||
        extrairListaInscricoes(inscricoesResponse).length;

      setTotalAcoesDisponiveis(canViewAcoesDisponiveis ? totalAcoes : 0);
      setTotalInscricoes(canViewMinhasInscricoes ? totalInscritos : 0);
      setTotalCertificadosDisponiveis(
        canViewCertificados ? Number(certificadosResponse?.totalizadores?.documentos_disponiveis) || 0 : 0,
      );

      const agendaNormalizada = canViewMinhasInscricoes
        ? extrairListaInscricoes(inscricoesResponse).map(normalizarInscricao)
        : [];
      setInscricoesAgenda(agendaNormalizada);
    } catch (error: any) {
      setMsgError(error?.message || "Nao foi possivel carregar os dados do dashboard.");
      setTotalAcoesDisponiveis(0);
      setTotalInscricoes(0);
      setTotalCertificadosDisponiveis(0);
      setInscricoesAgenda([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDashboard();
  }, [canViewAcoesDisponiveis, canViewCertificados, canViewMinhasInscricoes]);

  const markedDatesSet = useMemo(() => {
    const set = new Set<string>();

    inscricoesAgenda.forEach((item) => {
      const inicio = dayjs(item.dataInicio);
      const fim = dayjs(item.dataFim || item.dataInicio);

      if (!inicio.isValid()) return;

      let cursor = inicio.startOf("day");
      const limite = (fim.isValid() ? fim : inicio).startOf("day");

      while (cursor.isBefore(limite) || cursor.isSame(limite, "day")) {
        set.add(cursor.format("YYYY-MM-DD"));
        cursor = cursor.add(1, "day");
      }
    });

    return set;
  }, [inscricoesAgenda]);

  const inscricoesDoPeriodo = useMemo(() => {
    if (agendaView === "dia") {
      return inscricoesAgenda.filter((item) => {
        const inicio = dayjs(item.dataInicio);
        const fim = dayjs(item.dataFim || item.dataInicio);
        return (
          selectedDate.isSame(inicio, "day") ||
          selectedDate.isSame(fim, "day") ||
          (selectedDate.isAfter(inicio, "day") && selectedDate.isBefore(fim, "day"))
        );
      });
    }

    const inicioSemana = selectedDate.startOf("week");
    const fimSemana = selectedDate.endOf("week");
    const dentroDaSemana = (data: Dayjs) =>
      (data.isAfter(inicioSemana, "day") || data.isSame(inicioSemana, "day")) &&
      (data.isBefore(fimSemana, "day") || data.isSame(fimSemana, "day"));

    return inscricoesAgenda.filter((item) => {
      const inicio = dayjs(item.dataInicio);
      const fim = dayjs(item.dataFim || item.dataInicio);
      return (
        dentroDaSemana(inicio) ||
        dentroDaSemana(fim) ||
        (inicio.isBefore(inicioSemana, "day") && fim.isAfter(fimSemana, "day"))
      );
    });
  }, [agendaView, inscricoesAgenda, selectedDate]);

  const renderDay = (props: PickersDayProps<Dayjs>) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const hasInscricao = markedDatesSet.has(day.format("YYYY-MM-DD"));
    const isWeekend = day.day() === 0 || day.day() === 6;

    return (
      <Badge
        key={day.toString()}
        overlap="circular"
        variant={hasInscricao ? "dot" : undefined}
        color="primary"
      >
        <PickersDay
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          sx={{
            borderRadius: 1.5,
            border: hasInscricao ? "1px solid #5A81FA" : "1px solid transparent",
            color: isWeekend && !outsideCurrentMonth ? "#D43C3C" : undefined,
            "&.Mui-selected": {
              backgroundColor: "#5A81FA",
              color: "#FFF",
            },
          }}
          {...other}
        />
      </Badge>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography
        sx={{
          fontSize: { xs: "1.45rem", md: "1.75rem" },
          fontWeight: 700,
          mb: 3,
          textAlign: "left",
        }}
      >
        Ola, {getDisplayName() || "Usuario"}
      </Typography>

      {msgError && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{msgError}</Alert>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs:
                  canViewAcoesDisponiveis || canViewMinhasInscricoes || canViewCertificados
                    ? "1fr"
                    : undefined,
                md: "repeat(3, 1fr)",
              },
              gap: 2,
              mb: 3,
            }}
          >
            {canViewAcoesDisponiveis && (
              <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.12)", minHeight: 210 }}>
                <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <TaskOutlinedIcon sx={{ color: "#4D73E8", fontSize: 42 }} />
                    <Typography sx={{ color: "#4D73E8", fontWeight: 700, fontSize: "1.35rem" }}>
                      Ações disponíveis
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "2rem", fontWeight: 700, textAlign: "center", mb: 1, mt: "auto" }}>
                    {totalAcoesDisponiveis}
                  </Typography>
                  <Button
                    variant="contained"
                    className={styles_button.buttonSquarePrimary}
                    onClick={() => navigate("/realizar-inscricao")}
                  >
                    Realizar inscrições
                  </Button>
                </CardContent>
              </Card>
            )}

            {canViewMinhasInscricoes && (
              <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.12)", minHeight: 210 }}>
                <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <AccessTimeOutlinedIcon sx={{ color: "#E5891C", fontSize: 42 }} />
                    <Typography sx={{ color: "#E5891C", fontWeight: 700, fontSize: "1.35rem" }}>
                      Minhas Inscrições
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "2rem", fontWeight: 700, textAlign: "center", mb: 1, mt: "auto" }}>
                    {totalInscricoes}
                  </Typography>
                  <Button
                    variant="contained"
                    className={styles_button.buttonSquareOrange}
                    sx={{
                      backgroundColor: "#E5891C !important",
                      "&:hover": { backgroundColor: "#D27710 !important" },
                    }}
                    onClick={() => navigate("/gerenciar-inscricoes")}
                  >
                    Gerenciar minhas inscrições
                  </Button>
                </CardContent>
              </Card>
            )}

            {canViewCertificados && (
              <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.12)", minHeight: 210 }}>
                <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <WorkspacePremiumOutlinedIcon sx={{ color: "#4C8A62", fontSize: 42 }} />
                    <Typography sx={{ color: "#4C8A62", fontWeight: 700, fontSize: "1.35rem" }}>
                      Certificados e Declarações
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "2rem", fontWeight: 700, textAlign: "center", mb: 1, mt: "auto" }}>
                    {totalCertificadosDisponiveis}
                  </Typography>
                  <Button
                    variant="contained"
                    className={styles_button.buttonSquareSuccess}
                    onClick={() => navigate("/certificados-declaracoes")}
                  >
                    Acessar meus cert./dec.
                  </Button>
                </CardContent>
              </Card>
            )}
          </Box>

          <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}>
            <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{
                  fontSize: { xs: "1.35rem", md: "1.55rem" },
                  fontWeight: 700,
                  mb: 2,
                  textAlign: "left",
                }}
              >
                Minha agenda de qualificações
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "2.6fr 1fr" },
                  gap: 3,
                  alignItems: "start",
                }}
              >
                <Box
                  sx={{
                    border: "1px solid #DDE4FF",
                    borderRadius: 2,
                    p: { xs: 1.5, md: 2.5 },
                    backgroundColor: "#FFFFFF",
                    minHeight: { xs: 440, md: 530 },
                    display: "flex",
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                    <DateCalendar
                      value={selectedDate}
                      onChange={(newValue) => newValue && setSelectedDate(newValue)}
                      slots={{ day: renderDay as any }}
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        minHeight: { xs: 400, md: 480 },
                        "& .MuiDayCalendar-header": {
                          borderBottom: "1px solid #EEF1FF",
                          pb: 1,
                          mb: 1,
                        },
                        "& .MuiPickersCalendarHeader-root": {
                          px: 1,
                          mb: 1,
                        },
                        "& .MuiPickersFadeTransitionGroup-root": {
                          minHeight: { xs: 300, md: 360 },
                        },
                        "& .MuiDayCalendar-slideTransition": {
                          minHeight: { xs: 300, md: 360 },
                        },
                        "& .MuiDateCalendar-viewTransitionContainer": {
                          minHeight: { xs: 340, md: 400 },
                          overflowX: "hidden",
                          overflowY: "auto",
                          scrollbarWidth: "thin",
                          scrollbarColor: "transparent transparent",
                          "&::-webkit-scrollbar": {
                            width: 8,
                            backgroundColor: "transparent",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "transparent",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "transparent",
                            borderRadius: 8,
                          },
                        },
                        "& .MuiDayCalendar-monthContainer": {
                          minHeight: { xs: 340, md: 400 },
                        },
                        "& .MuiDayCalendar-weekDayLabel": {
                          fontWeight: 700,
                          color: "#7C7C7C",
                        },
                        "& .MuiDayCalendar-weekDayLabel:nth-of-type(1), & .MuiDayCalendar-weekDayLabel:nth-of-type(7)":
                          {
                            color: "#D43C3C",
                          },
                        "& .MuiPickersDay-root": {
                          width: 42,
                          height: 42,
                          fontSize: "0.95rem",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>

                <Box
                  sx={{
                    border: "1px solid #ECECEC",
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: "#FAFAFA",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <EventNoteOutlinedIcon fontSize="small" />
                    Sua agenda
                  </Typography>

                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={agendaView}
                    onChange={(_, value) => value && setAgendaView(value)}
                    sx={{ mb: 2 }}
                  >
                    <ToggleButton value="dia">Dia</ToggleButton>
                    <ToggleButton value="semana">Semana</ToggleButton>
                  </ToggleButtonGroup>

                  {inscricoesDoPeriodo.length === 0 ? (
                    <Typography sx={{ color: "#777" }}>
                      Sem inscricoes para o periodo selecionado.
                    </Typography>
                  ) : (
                    <Box sx={{ display: "grid", gap: 1.5, maxHeight: 360, overflowY: "auto" }}>
                      {inscricoesDoPeriodo.map((item) => (
                        <Box
                          key={`${item.acaoId}-${item.turmaId}`}
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            backgroundColor: "#FFF",
                            border: "1px solid #ECECEC",
                          }}
                        >
                          <Typography sx={{ fontWeight: 600 }}>{item.nomeAcao}</Typography>
                          <Typography sx={{ fontSize: "0.9rem", color: "#666" }}>
                            Turma: {item.nomeTurma}
                          </Typography>
                          <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>
                            {dayjs(item.dataInicio).format("DD/MM/YYYY")} ate{" "}
                            {dayjs(item.dataFim).format("DD/MM/YYYY")}
                          </Typography>
                          <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>
                            Modalidade: {item.modalidade}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
